// Phase 24 M4 — Google Sign-In button (Google Identity Services).
//
// Renders the official `<div>` Google injects + relies on
// `google.accounts.id.initialize` to call our callback with an `id_token`.
// When `clientId` is empty the button stays hidden so dev/CI doesn't
// need a Google project.

import { useEffect, useRef } from 'react';

interface GoogleIdWindow extends Window {
  google?: {
    accounts: {
      id: {
        initialize: (opts: {
          client_id: string;
          callback: (resp: { credential: string }) => void;
          ux_mode?: 'popup' | 'redirect';
          auto_select?: boolean;
        }) => void;
        renderButton: (
          el: HTMLElement,
          opts: { theme?: string; size?: string; text?: string; width?: number },
        ) => void;
      };
    };
  };
}

const SCRIPT_ID = 'gsi-script';
const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

function ensureScript(): Promise<void> {
  if (document.getElementById(SCRIPT_ID)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('google identity script failed to load'));
    document.head.appendChild(s);
  });
}

export interface GoogleSignInButtonProps {
  clientId: string;
  onIdToken: (idToken: string) => void | Promise<void>;
}

export function GoogleSignInButton({
  clientId,
  onIdToken,
}: GoogleSignInButtonProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!clientId || !ref.current) return;
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !ref.current) return;
        const w = window as GoogleIdWindow;
        if (!w.google?.accounts?.id) return;
        w.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => {
            void onIdToken(resp.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
        });
        w.google.accounts.id.renderButton(ref.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          width: 320,
        });
      })
      .catch(() => {
        /* swallow — button just won't render */
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, onIdToken]);

  if (!clientId) return null;
  return <div ref={ref} aria-label="google-sign-in" />;
}
